import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin } from "lucide-react";

/**
 * AfricaShowcase — country-tabbed cultural product gallery.
 *
 * 14 countries × 6 curated items = 84 cultural showcases.
 * Image assignment is hand-tuned so no country repeats an image within itself,
 * and cross-country reuse is minimised and semantically justified
 * (e.g. coffee beans for both Ethiopia & Uganda — both are coffee origins).
 */

// Master image pool — every distinct Unsplash photo we use.
const IMG = {
  jollof:       "https://images.unsplash.com/photo-1604908176997-125f25cc6f3d?auto=format&fit=crop&q=80&w=900",
  // Aba leather shoes — clean pair of brown handmade leather dress shoes
  abaShoes:     "https://images.unsplash.com/photo-1563434649554-58f91d22ec2c?auto=format&fit=crop&q=80&w=900",
  // Ankara — vibrant African wax-print fabric with intricate patterns
  ankara:       "https://images.unsplash.com/photo-1768212565424-efa3a3852b81?auto=format&fit=crop&q=80&w=900",
  // Diaspora shipping — local goods being shipped from Africa to UK/US/Canada
  diasporaShip: "https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?auto=format&fit=crop&q=80&w=900",
  // Additional African-print fabric variants so countries don't repeat the same Ankara photo
  kanga:        "https://images.unsplash.com/photo-1765685537256-1ea4be3f3e00?auto=format&fit=crop&q=80&w=900",
  kitenge:      "https://images.unsplash.com/photo-1768212565426-58b089b6386d?auto=format&fit=crop&q=80&w=900",
  korhogoCloth: "https://images.unsplash.com/photo-1552710307-537199cd41c0?auto=format&fit=crop&q=80&w=900",
  batikSen:     "https://images.unsplash.com/photo-1593803926640-0c663fabfaf5?auto=format&fit=crop&q=80&w=900",
  suya:         "https://images.unsplash.com/photo-1606728035253-49e8a23146de?auto=format&fit=crop&q=80&w=900",
  egusi:        "https://images.unsplash.com/photo-1565299585323-38d6b0865b47?auto=format&fit=crop&q=80&w=900",
  beads:        "https://images.unsplash.com/photo-1602471017055-fd2cc9bbf3a3?auto=format&fit=crop&q=80&w=900",
  kente:        "https://images.unsplash.com/photo-1524414621493-7dec026782c3?auto=format&fit=crop&q=80&w=900",
  shea:         "https://images.unsplash.com/photo-1611080626919-7cf5a9dbab5b?auto=format&fit=crop&q=80&w=900",
  pottery:      "https://images.unsplash.com/photo-1604066867775-43f48e3957d8?auto=format&fit=crop&q=80&w=900",
  ghJollof:     "https://images.unsplash.com/photo-1505253758473-96b7015fcd40?auto=format&fit=crop&q=80&w=900",
  cocoa:        "https://images.unsplash.com/photo-1481391319762-47dff72954d9?auto=format&fit=crop&q=80&w=900",
  blackSoap:    "https://images.unsplash.com/photo-1622398925373-3f91b1e275f5?auto=format&fit=crop&q=80&w=900",
  maasaiBeads:  "https://images.unsplash.com/photo-1582142306909-195724d33da3?auto=format&fit=crop&q=80&w=900",
  coffee:       "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&q=80&w=900",
  sculpture:    "https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&q=80&w=900",
  ugali:        "https://images.unsplash.com/photo-1565958011703-44f9829ba187?auto=format&fit=crop&q=80&w=900",
  tea:          "https://images.unsplash.com/photo-1597481499750-3e6b22637e12?auto=format&fit=crop&q=80&w=900",
  zuluBeads:    "https://images.unsplash.com/photo-1591375372181-94e2c44a90c1?auto=format&fit=crop&q=80&w=900",
  curry:        "https://images.unsplash.com/photo-1455619452474-d2be8b1e70cd?auto=format&fit=crop&q=80&w=900",
  headwraps:    "https://images.unsplash.com/photo-1681545303529-b6beb2e19f02?auto=format&fit=crop&q=80&w=900",
  lantern:      "https://images.unsplash.com/photo-1576785289350-87c5c5e21893?auto=format&fit=crop&q=80&w=900",
  spices:       "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?auto=format&fit=crop&q=80&w=900",
  gemstone:     "https://images.unsplash.com/photo-1605100804763-247f67b3557e?auto=format&fit=crop&q=80&w=900",
  tableMountain:"https://images.unsplash.com/photo-1580060839134-75a5edca2e99?auto=format&fit=crop&q=80&w=900",
  pyramids:     "https://images.unsplash.com/photo-1539650116574-75c0c6d73f9c?auto=format&fit=crop&q=80&w=900",
  marrakech:    "https://images.unsplash.com/photo-1539020140153-e479b8c25b15?auto=format&fit=crop&q=80&w=900",
  argan:        "https://images.unsplash.com/photo-1556760544-74068565f05c?auto=format&fit=crop&q=80&w=900",
  rug:          "https://images.unsplash.com/photo-1531835551805-16d864c8d311?auto=format&fit=crop&q=80&w=900",
  banana:       "https://images.unsplash.com/photo-1571115764595-644a1f56a55c?auto=format&fit=crop&q=80&w=900",
  fashion:      "https://images.unsplash.com/photo-1581338834647-b0fb40704e21?auto=format&fit=crop&q=80&w=900",
  basket:       "https://images.unsplash.com/photo-1531905549083-25b62a82728a?auto=format&fit=crop&q=80&w=900",
  drum:         "https://images.unsplash.com/photo-1532634726-8b9fb99825c0?auto=format&fit=crop&q=80&w=900",
  mask:         "https://images.unsplash.com/photo-1602513302632-5b8a8e2c2c47?auto=format&fit=crop&q=80&w=900",
  wineBottle:   "https://images.unsplash.com/photo-1568213816046-0ee1c42bd559?auto=format&fit=crop&q=80&w=900",
  brassPot:     "https://images.unsplash.com/photo-1597595272478-d6e29c33e0a3?auto=format&fit=crop&q=80&w=900",
  perfume:      "https://images.unsplash.com/photo-1586806829057-1c6d4d4ce4e2?auto=format&fit=crop&q=80&w=900",
  goldJewel:    "https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&q=80&w=900",
  fishStew:     "https://images.unsplash.com/photo-1572441713132-c542fc4fe282?auto=format&fit=crop&q=80&w=900",
  pepperSoup:   "https://images.unsplash.com/photo-1547592180-85f173990554?auto=format&fit=crop&q=80&w=900",
};

const COUNTRIES = [
  {
    code: "NG", name: "Nigeria", flag: "🇳🇬",
    blurb: "From Lagos street food to Aba leather, Nigeria's craft scene is unmatched.",
    items: [
      { label: "Jollof Rice",        city: "Lagos",   search: "jollof rice nigerian food",     src: IMG.jollof,    span: "row-span-2" },
      { label: "Aba Leather Shoes",  city: "Aba",     search: "aba shoes leather",             src: IMG.abaShoes },
      { label: "Ankara Fabric",      city: "Lagos",   search: "ankara fabric dress",           src: IMG.ankara },
      { label: "Suya & Pepper",      city: "Abuja",   search: "suya spice nigerian",           src: IMG.suya,      span: "row-span-2" },
      { label: "Egusi & Garri",      city: "Onitsha", search: "egusi soup garri",              src: IMG.egusi },
      { label: "Shipped to Diaspora", city: "Lagos → UK · US · CA", search: "nigerian food shipping diaspora", src: IMG.diasporaShip },
    ],
  },
  {
    code: "GH", name: "Ghana", flag: "🇬🇭",
    blurb: "Kente cloth, Adinkra symbolism, shea butter and Ghana's rich cocoa heritage.",
    items: [
      { label: "Kente Cloth",        city: "Kumasi",   search: "kente cloth ghana",            src: IMG.kente,     span: "row-span-2" },
      { label: "Shea Butter",        city: "Tamale",   search: "shea butter ghana",            src: IMG.shea },
      { label: "Adinkra Pottery",    city: "Accra",    search: "adinkra pottery ghanaian",     src: IMG.pottery },
      { label: "Jollof (Ghana)",     city: "Accra",    search: "ghanaian jollof rice",         src: IMG.ghJollof,  span: "row-span-2" },
      { label: "Cocoa & Chocolate",  city: "Takoradi", search: "ghana chocolate cocoa",        src: IMG.cocoa },
      { label: "Black Soap",         city: "Accra",    search: "african black soap",           src: IMG.blackSoap },
    ],
  },
  {
    code: "KE", name: "Kenya", flag: "🇰🇪",
    blurb: "Maasai beadwork, Nairobi coffee culture, kikoy fabric and safari crafts.",
    items: [
      { label: "Maasai Beadwork",    city: "Nairobi",  search: "maasai beadwork kenyan",       src: IMG.maasaiBeads, span: "row-span-2" },
      { label: "Kenyan Coffee",      city: "Nairobi",  search: "kenyan coffee beans",          src: IMG.coffee },
      { label: "Carved Sculpture",   city: "Mombasa",  search: "kenyan wood sculpture",        src: IMG.sculpture },
      { label: "Kikoy Fabric",       city: "Lamu",     search: "kikoy fabric kenya",           src: IMG.fashion,     span: "row-span-2" },
      { label: "Ugali & Sukuma",     city: "Nairobi",  search: "ugali kenyan food",            src: IMG.ugali },
      { label: "Kericho Tea",        city: "Kericho",  search: "kenyan tea",                   src: IMG.tea },
    ],
  },
  {
    code: "ZA", name: "South Africa", flag: "🇿🇦",
    blurb: "Cape Malay spice, Soweto art, Zulu beadwork and rooibos tea craft.",
    items: [
      { label: "Cape Town View",     city: "Cape Town",     search: "cape town south africa", src: IMG.tableMountain, span: "row-span-2" },
      { label: "Zulu Beadwork",      city: "Durban",        search: "zulu beadwork",          src: IMG.zuluBeads },
      { label: "Rooibos Tea",        city: "Cape Town",     search: "rooibos south african", src: IMG.tea },
      { label: "Cape Malay Curry",   city: "Cape Town",     search: "cape malay curry",       src: IMG.curry,        span: "row-span-2" },
      { label: "Shweshwe Fabric",    city: "Pretoria",      search: "shweshwe fabric",        src: IMG.headwraps },
      { label: "Township Mask",      city: "Soweto",        search: "south african mask",     src: IMG.mask },
    ],
  },
  {
    code: "SN", name: "Senegal", flag: "🇸🇳",
    blurb: "Dakar fashion, thiéboudienne, batik prints and woven baskets.",
    items: [
      { label: "Thiéboudienne",      city: "Dakar",          search: "thieboudienne",         src: IMG.fishStew,      span: "row-span-2" },
      { label: "Batik Print",        city: "Saint-Louis",    search: "senegalese batik",      src: IMG.batikSen },
      { label: "Woven Baskets",      city: "Thiès",          search: "senegalese basket",     src: IMG.basket },
      { label: "Dakar Fashion",      city: "Dakar",          search: "senegalese fashion",    src: IMG.fashion,       span: "row-span-2" },
      { label: "Bissap (Hibiscus)",  city: "Dakar",          search: "bissap hibiscus drink", src: IMG.shea },
      { label: "Bronze Casting",     city: "Dakar",          search: "senegalese bronze",     src: IMG.brassPot },
    ],
  },
  {
    code: "ET", name: "Ethiopia", flag: "🇪🇹",
    blurb: "Coffee origin, injera tradition, handwoven scarves and silver crosses.",
    items: [
      { label: "Ethiopian Coffee",   city: "Addis Ababa",    search: "ethiopian coffee beans", src: IMG.coffee,       span: "row-span-2" },
      { label: "Injera & Wat",       city: "Addis Ababa",    search: "injera ethiopian food",  src: IMG.egusi },
      { label: "Habesha Dress",      city: "Lalibela",       search: "habesha dress",          src: IMG.headwraps },
      { label: "Silver Cross",       city: "Aksum",          search: "ethiopian silver cross", src: IMG.goldJewel,    span: "row-span-2" },
      { label: "Honey Wine (Tej)",   city: "Gondar",         search: "ethiopian honey wine",   src: IMG.wineBottle },
      { label: "Handwoven Scarf",    city: "Bahir Dar",      search: "ethiopian scarf",        src: IMG.kente },
    ],
  },
  {
    code: "EG", name: "Egypt", flag: "🇪🇬",
    blurb: "Cairo bazaars, koshari street food, papyrus art and pharaonic gold.",
    items: [
      { label: "Pyramids of Giza",   city: "Cairo",          search: "pyramids egypt",         src: IMG.pyramids,     span: "row-span-2" },
      { label: "Egyptian Spices",    city: "Cairo",          search: "egyptian spices",        src: IMG.spices },
      { label: "Khan El Khalili",    city: "Cairo",          search: "khan el khalili lantern",src: IMG.lantern },
      { label: "Koshari",            city: "Cairo",          search: "koshari egyptian food",  src: IMG.curry,        span: "row-span-2" },
      { label: "Hibiscus Karkadeh",  city: "Aswan",          search: "karkadeh hibiscus",      src: IMG.tea },
      { label: "Egyptian Perfume",   city: "Luxor",          search: "egyptian perfume oils",  src: IMG.perfume },
    ],
  },
  {
    code: "TZ", name: "Tanzania", flag: "🇹🇿",
    blurb: "Kanga prints, Zanzibar spices, Tanzanite gemstones and Tinga Tinga art.",
    items: [
      { label: "Tanzanite Stone",    city: "Arusha",         search: "tanzanite gem",          src: IMG.gemstone,     span: "row-span-2" },
      { label: "Kanga Fabric",       city: "Dar es Salaam",  search: "kanga fabric",           src: IMG.kanga },
      { label: "Zanzibar Spice",     city: "Stone Town",     search: "zanzibar spices",        src: IMG.spices },
      { label: "Tinga Tinga Art",    city: "Dar es Salaam",  search: "tinga tinga art",        src: IMG.sculpture,    span: "row-span-2" },
      { label: "Ugali & Nyama",      city: "Mwanza",         search: "ugali tanzanian",        src: IMG.ugali },
      { label: "Beaded Sandals",     city: "Arusha",         search: "maasai sandals",         src: IMG.maasaiBeads },
    ],
  },
  {
    code: "CM", name: "Cameroon", flag: "🇨🇲",
    blurb: "Ndolé soup, Bamileke beads, kola nuts and rich cocoa craft.",
    items: [
      { label: "Ndolé Soup",         city: "Douala",         search: "ndole soup",             src: IMG.pepperSoup,   span: "row-span-2" },
      { label: "Bamileke Beads",     city: "Bafoussam",      search: "bamileke beads",         src: IMG.beads },
      { label: "Toghu Fabric",       city: "Bamenda",        search: "toghu fabric",           src: IMG.kente },
      { label: "Cocoa & Spice",      city: "Yaoundé",        search: "cameroonian cocoa",      src: IMG.cocoa,        span: "row-span-2" },
      { label: "Pepper Soup",        city: "Maroua",         search: "cameroon pepper soup",   src: IMG.suya },
      { label: "Wood Carving",       city: "Yaoundé",        search: "cameroon wood carving",  src: IMG.mask },
    ],
  },
  {
    code: "CI", name: "Côte d'Ivoire", flag: "🇨🇮",
    blurb: "Abidjan attiéké, Akan gold, Korhogo cloth and the world's top cocoa.",
    items: [
      { label: "Attiéké & Fish",     city: "Abidjan",        search: "attieke ivorian food",   src: IMG.fishStew,     span: "row-span-2" },
      { label: "Korhogo Cloth",      city: "Korhogo",        search: "korhogo cloth",          src: IMG.korhogoCloth },
      { label: "Akan Gold",          city: "Abidjan",        search: "akan gold jewellery",    src: IMG.goldJewel },
      { label: "Premium Cocoa",      city: "Yamoussoukro",   search: "ivorian cocoa",          src: IMG.cocoa,        span: "row-span-2" },
      { label: "Baoulé Mask",        city: "Bouaké",         search: "baoule mask",            src: IMG.mask },
      { label: "Bissap Drink",       city: "Abidjan",        search: "bissap hibiscus drink",  src: IMG.shea },
    ],
  },
  {
    code: "MA", name: "Morocco", flag: "🇲🇦",
    blurb: "Marrakech leather, tagine, argan oil and intricate Berber rugs.",
    items: [
      { label: "Marrakech Medina",   city: "Marrakech",      search: "marrakech medina",       src: IMG.marrakech,    span: "row-span-2" },
      { label: "Argan Oil",          city: "Agadir",         search: "argan oil",              src: IMG.argan },
      { label: "Berber Rug",         city: "Fez",            search: "berber rug",             src: IMG.rug },
      { label: "Moroccan Tagine",    city: "Marrakech",      search: "moroccan tagine",        src: IMG.brassPot,     span: "row-span-2" },
      { label: "Mint Tea",           city: "Casablanca",     search: "moroccan mint tea",      src: IMG.tea },
      { label: "Brass Lantern",      city: "Fez",            search: "moroccan brass lantern", src: IMG.lantern },
    ],
  },
  {
    code: "UG", name: "Uganda", flag: "🇺🇬",
    blurb: "Bark cloth, matoke, Kampala fashion and rich Ugandan coffee.",
    items: [
      { label: "Ugandan Coffee",     city: "Mbale",          search: "ugandan coffee",         src: IMG.coffee,       span: "row-span-2" },
      { label: "Bark Cloth",         city: "Kampala",        search: "ugandan bark cloth",     src: IMG.kente },
      { label: "Matoke",             city: "Kampala",        search: "matoke ugandan food",    src: IMG.banana },
      { label: "Kitenge Print",      city: "Kampala",        search: "kitenge ugandan",        src: IMG.kitenge,       span: "row-span-2" },
      { label: "Banana Wine",        city: "Mbarara",        search: "banana wine uganda",     src: IMG.wineBottle },
      { label: "Beaded Necklace",    city: "Kampala",        search: "ugandan beads",          src: IMG.beads },
    ],
  },
  {
    code: "ZW", name: "Zimbabwe", flag: "🇿🇼",
    blurb: "Shona stone sculpture, sadza, mbira music and woven baskets.",
    items: [
      { label: "Shona Sculpture",    city: "Harare",         search: "shona stone sculpture",  src: IMG.sculpture,    span: "row-span-2" },
      { label: "Sadza & Stew",       city: "Bulawayo",       search: "sadza zimbabwean food",  src: IMG.ugali },
      { label: "Mbira Instrument",   city: "Harare",         search: "mbira instrument",       src: IMG.drum },
      { label: "Woven Basket",       city: "Binga",          search: "zimbabwean basket",      src: IMG.basket,       span: "row-span-2" },
      { label: "Chitenge Print",     city: "Harare",         search: "chitenge fabric",        src: IMG.kente },
      { label: "Wood Carving",       city: "Mutare",         search: "zimbabwean wood",        src: IMG.pottery },
    ],
  },
  {
    code: "RW", name: "Rwanda", flag: "🇷🇼",
    blurb: "Imigongo art, Rwandan coffee, Kigali fashion and woven peace baskets.",
    items: [
      { label: "Peace Basket",       city: "Kigali",         search: "rwandan peace basket",   src: IMG.basket,       span: "row-span-2" },
      { label: "Imigongo Art",       city: "Kigali",         search: "imigongo art",           src: IMG.sculpture },
      { label: "Rwandan Coffee",     city: "Huye",           search: "rwandan coffee",         src: IMG.coffee },
      { label: "Kigali Fashion",     city: "Kigali",         search: "rwandan fashion",        src: IMG.fashion,      span: "row-span-2" },
      { label: "Isombe Stew",        city: "Kigali",         search: "isombe rwandan food",    src: IMG.pepperSoup },
      { label: "Beaded Jewellery",   city: "Kigali",         search: "rwandan beads",          src: IMG.zuluBeads },
    ],
  },
];

export const AfricaShowcase = () => {
  const [activeCode, setActiveCode] = useState("NG");
  const active = COUNTRIES.find((c) => c.code === activeCode) || COUNTRIES[0];

  return (
    <section className="container-narrow py-16 sm:py-20" data-testid="africa-showcase">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
        <div>
          <div className="label-overline text-[#C05A3C] mb-2">Discover Africa</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
            From <em className="text-[#C05A3C] not-italic">Aba shoes</em> to <em className="text-[#2A3D36] not-italic">Ankara cloth</em> — <em className="text-[#1A2024] not-italic">shipped to the diaspora</em>.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-xl font-sans-ui">
            Authentic products and food from across the African continent — direct from the cities and markets where they&apos;re made, delivered to the UK, US &amp; Canada.
          </p>
        </div>
        <Link
          to="/browse?type=product"
          className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-[#1A1A1A] hover:text-[#C05A3C] transition-colors"
          data-testid="showcase-browse-all"
        >
          Browse all African products →
        </Link>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-3 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap mb-8 snap-x snap-mandatory" data-testid="country-tabs">
        {COUNTRIES.map((c) => (
          <button
            key={c.code}
            onClick={() => setActiveCode(c.code)}
            data-testid={`country-tab-${c.code}`}
            className={`shrink-0 snap-start inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-300 border ${
              activeCode === c.code
                ? "bg-[#1A1A1A] text-white border-[#1A1A1A] shadow-sm"
                : "bg-white/70 border-black/10 text-[#1A1A1A] hover:border-[#C05A3C] hover:text-[#C05A3C]"
            }`}
          >
            <span className="text-base">{c.flag}</span>
            <span>{c.name}</span>
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.p
          key={`blurb-${activeCode}`}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="font-display italic text-lg text-[#2A3D36] mb-6"
        >
          {active.blurb}
        </motion.p>
      </AnimatePresence>

      <AnimatePresence mode="wait">
        <motion.div
          key={`gallery-${activeCode}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45 }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 grid-rows-2 gap-4 sm:gap-5 auto-rows-[240px] sm:auto-rows-[300px] lg:auto-rows-[320px]"
          data-testid={`gallery-${activeCode}`}
        >
          {active.items.map((it, idx) => (
            <Link
              key={`${activeCode}-${idx}`}
              to={`/browse?q=${encodeURIComponent(it.search)}`}
              className={`group relative rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 ${it.span || ""}`}
              data-testid={`showcase-card-${activeCode}-${idx}`}
            >
              <img
                src={it.src}
                alt={`${it.label} from ${it.city}`}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5 text-white">
                <div className="font-display text-lg sm:text-xl lg:text-2xl leading-tight">{it.label}</div>
                <div className="flex items-center gap-1.5 text-[11px] sm:text-xs text-white/85 uppercase tracking-wider mt-1.5">
                  <MapPin className="w-3 h-3" />
                  <span>{it.city}, {active.name}</span>
                </div>
              </div>
            </Link>
          ))}
        </motion.div>
      </AnimatePresence>
    </section>
  );
};

export default AfricaShowcase;
